/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview State, effects, and handlers for the appointment registration modal.
 * @module hooks/useAppointmentRegistrationForm
 */

import { useCallback, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  getCompanyFieldValue,
  PROFILE_EDITABLE_FIELDS,
  selectStyles,
} from "../utils/common.js";
import { syncCertificationMissingFields } from "../utils/appointmentRegistrationUtils.js";
import { isRegistrationSubmitDisabled } from "../logic/appointments/appointmentRegistrationDate.js";
import {
  applyFieldChangeToCompany,
  getMissingFieldUpdateKind,
  isFieldValueMissing,
} from "../logic/appointments/appointmentRegistrationFieldChange.js";
import useAppointmentRegistrationLifecycle from "./useAppointmentRegistrationLifecycle.js";
import useAppointmentRegistrationSectionHandlers from "./useAppointmentRegistrationSectionHandlers.js";
import useAppointmentRegistrationSubmit from "./useAppointmentRegistrationSubmit.js";

/**
 * Encapsulates appointment registration modal state and side effects.
 * @param {Object} props - Same props as `AppointmentRegistration`
 * @returns {Object} View-model for `AppointmentRegistrationView`
 */
const useAppointmentRegistrationForm = ({
  dialogRef,
  selectedRequirement,
  participant,
  eventData,
  currentUser,
  open,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [company, setCompany] = useState(
    selectedRequirement?.datosEmpresaUsuario || null,
  );
  const [selectedProfile, setSelectedProfile] = useState(
    selectedRequirement?.datosEmpresaUsuario?.perfilSeleccionado ?? 0,
  );
  const [category, setCategory] = useState(null);
  const [missingRequiredFields, setMissingRequiredFields] = useState(
    selectedRequirement?.missingRequiredFields || null,
  );
  const [missingOptionalFields, setMissingOptionalFields] = useState(
    selectedRequirement?.missingOptionalFields || null,
  );
  const [formData, setFormData] = useState({});
  const [initLoading, setInitLoading] = useState(false);
  const [locationReferenceData, setLocationReferenceData] = useState(null);
  const confirmationDialogRef = useRef(null);

  const profileOptions = useMemo(
    () => [
      { value: 0, label: t("pages.companyProfile.profile1") },
      { value: 1, label: t("pages.companyProfile.profile2") },
      { value: 2, label: t("pages.companyProfile.profile3") },
    ],
    [t],
  );

  const profileSelectStyles = useMemo(
    () => ({
      ...selectStyles,
      menu: (base) => ({ ...base, zIndex: 9999 }),
    }),
    [],
  );

  const displayCompany = useMemo(() => {
    if (!company) {
      return null;
    }
    const merged = { ...company };
    PROFILE_EDITABLE_FIELDS.forEach((key) => {
      merged[key] = getCompanyFieldValue(company, key, selectedProfile, "");
    });
    return merged;
  }, [company, selectedProfile]);

  const {
    loading,
    showConfirmationModal,
    isCheckingAppointments,
    showRequiredFieldErrors,
    checkAppointmentsAndProceed: runCapacityCheck,
    handleConfirmationModalConfirm: confirmCapacityModal,
    handleConfirmationModalCancel,
  } = useAppointmentRegistrationSubmit({
    currentUser,
    participant,
    selectedRequirement,
    displayCompany,
    company,
    formData,
    missingRequiredFields,
    eventData,
    onSubmit,
  });

  const isSubmitting = loading || isCheckingAppointments || initLoading;
  const isFormLocked = isSubmitting || showConfirmationModal;

  const resetFormStateForRequirement = useCallback(
    (companyData) => {
      setCompany(companyData);
      setSelectedProfile(companyData?.perfilSeleccionado ?? 0);
      setMissingRequiredFields(
        selectedRequirement?.missingRequiredFields || null,
      );
      setMissingOptionalFields(
        selectedRequirement?.missingOptionalFields || null,
      );
      setFormData({});
    },
    [selectedRequirement],
  );

  const applyInitializedData = useCallback(
    ({
      locationReferenceData: locationData,
      category: categoryData,
      refreshedRequirement,
    }) => {
      setLocationReferenceData(locationData);
      setCategory(categoryData);
      if (refreshedRequirement) {
        setCompany(refreshedRequirement.company);
        setSelectedProfile(refreshedRequirement.selectedProfile);
        setMissingRequiredFields(refreshedRequirement.missingRequiredFields);
        setMissingOptionalFields(refreshedRequirement.missingOptionalFields);
      }
    },
    [],
  );

  useAppointmentRegistrationLifecycle({
    dialogRef,
    open,
    onClose,
    isFormLocked,
    selectedRequirement,
    participant,
    resetFormStateForRequirement,
    applyInitializedData,
    setInitLoading,
  });

  const getFieldValidation = useCallback(
    (fieldKey) =>
      Boolean(
        showRequiredFieldErrors &&
          missingRequiredFields &&
          missingRequiredFields[fieldKey] === true,
      ),
    [showRequiredFieldErrors, missingRequiredFields],
  );

  const handleFieldChange = useCallback(
    (section, field, value) => {
      const isMissing = isFieldValueMissing(value);
      const updateKind = getMissingFieldUpdateKind(
        field,
        missingRequiredFields,
        missingOptionalFields,
      );

      if (updateKind === "required") {
        setMissingRequiredFields((prev) =>
          prev && Object.hasOwn(prev, field)
            ? { ...prev, [field]: isMissing }
            : prev,
        );
      } else if (updateKind === "optional") {
        setMissingOptionalFields((prev) =>
          prev && Object.hasOwn(prev, field)
            ? { ...prev, [field]: isMissing }
            : prev,
        );
      } else if (updateKind === "certifications" && Array.isArray(value)) {
        syncCertificationMissingFields(value, setMissingRequiredFields);
        syncCertificationMissingFields(value, setMissingOptionalFields);
      } else {
        // Profile and other sections do not update missing-field maps here.
      }

      setFormData((prev) => ({
        ...prev,
        [section]: { ...prev[section], [field]: value },
      }));

      if (company) {
        setCompany((prev) =>
          applyFieldChangeToCompany(prev, selectedProfile, field, value),
        );
      }
    },
    [company, missingOptionalFields, missingRequiredFields, selectedProfile],
  );

  const sectionHandlers = useAppointmentRegistrationSectionHandlers(
    handleFieldChange,
  );

  const handleRequestClose = useCallback(() => {
    if (!isFormLocked) {
      onClose();
    }
  }, [isFormLocked, onClose]);

  const handleProfileSelectChange = useCallback((option) => {
    setSelectedProfile(option.value);
  }, []);

  const checkAppointmentsAndProceed = useCallback(
    () => runCapacityCheck(setCompany),
    [runCapacityCheck],
  );

  const handleConfirmationModalConfirm = useCallback(
    () => confirmCapacityModal(setCompany),
    [confirmCapacityModal],
  );

  const selectedProfileOption = useMemo(
    () => profileOptions.find((option) => option.value === selectedProfile),
    [profileOptions, selectedProfile],
  );

  return {
    t,
    dialogRef,
    selectedRequirement,
    company,
    displayCompany,
    selectedProfile,
    category,
    missingRequiredFields,
    locationReferenceData,
    isFormLocked,
    isSubmitting,
    loading,
    showConfirmationModal,
    confirmationDialogRef,
    profileOptions,
    profileSelectStyles,
    selectedProfileOption,
    getFieldValidation,
    handleRequestClose,
    handleProfileSelectChange,
    checkAppointmentsAndProceed,
    handleConfirmationModalConfirm,
    handleConfirmationModalCancel,
    isButtonDisabled: isRegistrationSubmitDisabled({
      isSubmitting,
      tipoDeUsuario: currentUser?.tipoDeUsuario,
      fechaLimiteRegistro: eventData?.fechaLimiteRegistro,
    }),
    ...sectionHandlers,
  };
};

export default useAppointmentRegistrationForm;
